/* eslint-disable react-hooks/exhaustive-deps */
import React, {useContext, useEffect, useRef} from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { GreenroomState as GS } from '../../stores/types/types';
import { VideoStream } from '../types/videos';
import blackImage from '../../images/black-image.jpeg';
import { cleanupVideoStreamElement } from '../../utils/video/video-streams';
import useDropArea from '../../hooks/use-dnd/use-drop-area';
import type { DropCallback } from '../../stores/types/user-interface';
import { DRAG_TYPE } from '../../stores/reducers/user-interface';
import useShotMemos from '../../hooks/use-shot-memos';
import { AVContext } from '../../contexts/audio-video';
import { appLogger } from '../../utils/datadog';
import { ROOM_STATES } from '../../stores/reducers/settings';
import FeedPresenterSafetyPhoto from './feed-presenter-safety-photo.tsx';

interface Props {
	video: VideoStream | undefined;
	feedPosition: number;
	grUser: number;
	dropCallback: DropCallback;
}

export default function FeedPresenter(props: Props) {
	const {
		video,
		feedPosition,
		grUser,
		dropCallback,
	} = props;

	const host = useSelector((s: GS) => s.SessionReducer.host);
	const user = useSelector((s: GS) => s.SessionReducer.user);
	const embed = useSelector((s: GS) => s.SessionReducer.embed);
	const backstageCanvasVolume = useSelector((s: GS) => s.MeetingReducer.backstageCanvasVolume);
	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);

	const videoElRef = useRef<HTMLVideoElement>(null);

	const { getAV } = useContext(AVContext);
	const { video: hasVideo } = getAV(video?.id);

	const { isOnCanvas } = useShotMemos();

	const { isOver, dropRef } = useDropArea({
		accepts: DRAG_TYPE.FEEDS,
		dropCallback,
		dropAreaData: { feedPosition },
	});

	async function playPresenterFeed(srcObject: MediaStream) {
		if (!videoElRef.current) return;

		videoElRef.current.srcObject = srcObject;
		videoElRef.current.volume = 0;
		if (!srcObject.active) return;

		try {
			await videoElRef.current.play();
			if (videoElRef.current?.paused) {
				try {
					await videoElRef.current.play();
				} catch (e: any) {
					appLogger.error(
						'video el play request error',
						{ location: 'feed-presenter second' },
						e,
					);
				}
			}
		} catch (e: any) {
			appLogger.error(
				'video el play request error',
				{ location: 'feed-presenter first' },
				e,
			);
		}

		cleanupVideoStreamElement(videoElRef.current);
	}

	const videoDebounce = useRef<NodeJS.Timeout | null>(null);

	// This attaches connectedPresenterVideo.srcObject to our video DOM element.
	// The srcObject is derived from OpenTok's publisher/subscriber object.
	// This has been known to cause issues where the user's video does not appear.
	// (Presenter appears as a black feed instead.)
	// green-4902
	useEffect(() => {
		if (videoElRef.current && video && video?.srcObject !== videoElRef.current.srcObject) {
			if (videoDebounce.current) { clearTimeout(videoDebounce.current); }
			videoDebounce.current = setTimeout(() => {
				playPresenterFeed(video.srcObject);
			}, 100);
		}
	}, [video?.srcObject, videoElRef.current]);

	useEffect(() => {
		if (videoElRef.current) {
			videoElRef.current.volume = (
				(isOnCanvas || embed || roomState === ROOM_STATES.EDITING)
					? 1
					: backstageCanvasVolume
			);
		}
	}, [isOnCanvas, roomState, backstageCanvasVolume]);

	return (
		<div ref={dropRef} className='feed-drop-area'>
			{isOver && <div className="dnd-highlighted" />}
			{video && video.srcObject && video?.srcObject.active
				? <>
					<video
						key={video.srcObject.id + feedPosition}
						className={classNames(!host && !embed && video.profile === user?.gr_user && 'mirrored')}
						muted={true}
						poster={blackImage}
						autoPlay={true}
						playsInline
						ref={videoElRef}
						onLoadedData={() => {
							// Force play on presenter video elements to avoid a
							// paused OpenTok publisher/subscriber feed.
							// green-4902
							videoElRef.current?.play().catch((e) => {
								appLogger.error('video el play request error', {
									location: 'feed-presenter third',
								}, e);
							});
						}}
					/>
					<FeedPresenterSafetyPhoto active={!hasVideo} grUserId={grUser} videoId={video.id} />
				</>
				: <FeedPresenterSafetyPhoto active={true} grUserId={grUser} videoId={video?.id ?? ''} />
			}
		</div>
	);
}
